import { useRef } from "react";
import styled from "styled-components";
import { motion, useInView } from "framer-motion";
import {
  FaInstagram,
  FaWhatsapp,
  FaPinterestP,
  FaTiktok,
} from "react-icons/fa";
import devLogo from "../../assets/asitesLogoDL.png";
import { Link } from "react-router-dom";

// ─── Replace with real links ───────────────────────────────────────────
const WHATSAPP_URL = "https://wa.me/4917631664395";
const INSTAGRAM_URL = "https://www.instagram.com/makeup.by.laureta/";

// ─── Styled ────────────────────────────────────────────────────────────
const Section = styled.section`
  position: relative;
  width: 100%;
  background: var(--ink);
  overflow: hidden;
`;

const Grain = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  /* background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E"); */
`;

const TopRule = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 7vw;
  right: 7vw;
  height: 1px;
  background: linear-gradient(
    90deg,
    var(--terracotta),
    var(--gold) 40%,
    transparent
  );
  opacity: 0.2;
  transform-origin: left;
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 10rem 7vw 0;
`;

// ── Heading ────────────────────────────────────────────────────────────
const EyebrowRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.8rem;
`;
const EyebrowDash = styled(motion.span)`
  display: block;
  width: 32px;
  height: 1px;
  background: var(--terracotta);
  opacity: 0.8;
`;
const Eyebrow = styled(motion.span)`
  font-family: var(--font-sans);
  font-size: 0.6rem;
  font-weight: 300;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--terracotta);
`;

const HeadingWrap = styled.div`
  overflow: visible;
  margin-bottom: 0.1rem;
`;
const Heading = styled(motion.h2)`
  font-family: var(--font-serif);
  font-size: clamp(3rem, 6vw, 6.5rem);
  font-weight: 300;
  line-height: 0.9;
  color: var(--cream);
  letter-spacing: -0.02em;
`;
const HeadingItalic = styled(Heading)`
  font-style: italic;
  color: var(--warm-tan);
`;

const Tagline = styled(motion.p)`
  font-family: var(--font-sans);
  font-size: clamp(0.8rem, 1vw, 0.9rem);
  font-weight: 200;
  letter-spacing: 0.07em;
  line-height: 1.9;
  color: rgba(247, 243, 238, 0.4);
  max-width: 38ch;
  margin-top: 2.5rem;
`;

// ── Two big CTA buttons ────────────────────────────────────────────────
const CtaRow = styled(motion.div)`
  display: flex;
  gap: 1.2rem;
  margin-top: 4rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const CtaBtn = styled(motion.a)`
  display: flex;
  align-items: center;
  gap: 1rem;
  text-decoration: none;
  padding: 1.4rem 3rem;
  border: 1px solid
    ${({ $primary }) =>
      $primary ? "var(--terracotta)" : "rgba(247,243,238,0.15)"};
  background: ${({ $primary }) =>
    $primary ? "var(--terracotta)" : "transparent"};
  position: relative;
  overflow: hidden;
  transition: border-color 0.4s;

  /* Sweep fill on hover for secondary */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: ${({ $primary }) =>
      $primary ? "var(--mocha)" : "rgba(247,243,238,0.05)"};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
    z-index: 0;
  }
  &:hover::after {
    transform: scaleX(1);
  }
  &:hover {
    border-color: ${({ $primary }) =>
      $primary ? "var(--mocha)" : "rgba(247,243,238,0.3)"};
  }
`;

const CtaBtnIcon = styled.span`
  font-size: 1.2rem;
  color: ${({ $primary }) =>
    $primary ? "rgba(247,243,238,0.9)" : "rgba(247,243,238,0.4)"};
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
`;

const CtaBtnLabel = styled.span`
  position: relative;
  z-index: 1;
  font-family: var(--font-sans);
  font-size: 0.68rem;
  font-weight: 400;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ $primary }) =>
    $primary ? "var(--cream)" : "rgba(247,243,238,0.5)"};
`;

// ── Other socials (small row below) ───────────────────────────────────
const OtherSocials = styled(motion.div)`
  display: flex;
  gap: 0.7rem;
  margin-top: 2.5rem;
  display: none;
`;

const SmallSocial = styled(motion.a)`
  width: 38px;
  height: 38px;
  border: 1px solid rgba(247, 243, 238, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.733);
  font-size: 0.85rem;
  text-decoration: none;
  transition:
    border-color 0.3s,
    color 0.3s;
  &:hover {
    border-color: var(--terracotta);
    color: var(--warm-tan);
  }
`;

// ── Divider before footer ──────────────────────────────────────────────
const SectionDivider = styled(motion.div)`
  width: 100%;
  height: 1px;
  background: rgba(247, 243, 238, 0.07);
  margin-top: 8rem;
`;

// ── Footer ─────────────────────────────────────────────────────────────
const Footer = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.6rem 7vw;
  flex-wrap: wrap;
  gap: 1rem;
  border-top: 1px solid rgba(247, 243, 238, 0.05);
`;

const FooterLeft = styled(motion.span)`
  font-family: var(--font-sans);
  font-size: 0.55rem;
  font-weight: 300;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(247, 243, 238, 0.705);
`;

const FooterRight = styled(motion.span)`
  font-family: var(--font-serif);
  font-size: 0.72rem;
  font-weight: 300;
  font-style: italic;
  color: rgba(247, 243, 238, 0.733);
`;

// ── Dev credit strip ───────────────────────────────────────────────────
const DevStrip = styled(motion.div)`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  /* gap: 1.2rem; */
  padding: 1.2rem 7vw 2rem;
`;

const DevLabel = styled.span`
  font-family: var(--font-sans);
  font-size: 0.5rem;
  font-weight: 300;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  color: rgba(247, 243, 238, 0.918);
  white-space: nowrap;
`;

const DevDivider = styled.div`
  width: 20px;
  height: 1px;
  background: rgba(247, 243, 238, 0.08);
  flex-shrink: 0;
`;

const DevLogoLink = styled(motion.a)`
  display: flex;
  align-items: center;
  opacity: 0.92;
  transition: opacity 0.35s ease;
  text-decoration: none;
  filter: grayscale(1);

  &:hover {
    opacity: 0.55;
  }

  img {
    height: 48px;
    width: auto;
    display: block;
  }
`;
const Legal = styled.p`
  color: #b1b1b1;
  text-align: center;
  font-size: 14px;
  margin-bottom: 1em;
  text-decoration: underline;
`;

// ─── Main ──────────────────────────────────────────────────────────────
export default function ContactSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10%" });

  const revealUp = {
    hidden: { y: "105%" },
    show: { y: "0%", transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] } },
  };

  const fadeUp = (delay = 0) => ({
    hidden: { opacity: 0, y: 18 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
    },
  });

  return (
    <Section>
      <Grain />

      <TopRule
        initial={{ scaleX: 0 }}
        animate={inView ? { scaleX: 1 } : {}}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
      />

      <Inner ref={ref}>
        {/* Eyebrow */}
        <EyebrowRow>
          <EyebrowDash
            initial={{ scaleX: 0 }}
            animate={inView ? { scaleX: 1 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
            style={{ transformOrigin: "left" }}
          />
          <Eyebrow
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            Get in touch
          </Eyebrow>
          <EyebrowDash
            initial={{ scaleX: 0 }}
            animate={inView ? { scaleX: 1 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
            style={{ transformOrigin: "right" }}
          />
        </EyebrowRow>

        {/* Heading */}
        <HeadingWrap>
          <Heading
            variants={revealUp}
            initial="hidden"
            animate={inView ? "show" : "hidden"}
            transition={{ delay: 0.2 }}
          >
            Let's create
          </Heading>
        </HeadingWrap>
        <HeadingWrap>
          <HeadingItalic
            as={motion.h2}
            variants={revealUp}
            initial="hidden"
            animate={inView ? "show" : "hidden"}
            transition={{ delay: 0.32 }}
          >
            something beautiful.
          </HeadingItalic>
        </HeadingWrap>

        {/* Tagline */}
        <Tagline
          variants={fadeUp(0.5)}
          initial="hidden"
          animate={inView ? "show" : "hidden"}
        >
          Whether you have a date in mind or just a feeling — reach out on
          WhatsApp or Instagram and let's talk.
        </Tagline>

        {/* Main CTA buttons */}
        <CtaRow
          variants={fadeUp(0.62)}
          initial="hidden"
          animate={inView ? "show" : "hidden"}
        >
          {/* WhatsApp — primary */}
          <CtaBtn
            $primary
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.97 }}
            transition={{ duration: 0.25 }}
          >
            <CtaBtnIcon $primary>
              <FaWhatsapp />
            </CtaBtnIcon>
            <CtaBtnLabel $primary>Message on WhatsApp</CtaBtnLabel>
          </CtaBtn>

          {/* Instagram — secondary */}
          <CtaBtn
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.97 }}
            transition={{ duration: 0.25 }}
          >
            <CtaBtnIcon>
              <FaInstagram />
            </CtaBtnIcon>
            <CtaBtnLabel>DM on Instagram</CtaBtnLabel>
          </CtaBtn>
        </CtaRow>

        {/* Other socials */}
        <OtherSocials
          variants={fadeUp(0.76)}
          initial="hidden"
          animate={inView ? "show" : "hidden"}
        >
          {[
            { icon: <FaPinterestP />, href: "#", label: "Pinterest" },
            { icon: <FaTiktok />, href: "#", label: "TikTok" },
          ].map((s) => (
            <SmallSocial
              key={s.label}
              href={s.href}
              target="_blank"
              aria-label={s.label}
              whileHover={{ y: -2 }}
              transition={{ duration: 0.2 }}
            >
              {s.icon}
            </SmallSocial>
          ))}
        </OtherSocials>

        <SectionDivider
          initial={{ scaleX: 0 }}
          animate={inView ? { scaleX: 1 } : {}}
          transition={{ delay: 0.9, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          style={{ transformOrigin: "center" }}
        />
      </Inner>

      {/* ── Main footer row ── */}
      <Footer>
        <FooterLeft
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 1, duration: 0.8 }}
        >
          © {new Date().getFullYear()} Laureta Martini · All rights reserved
        </FooterLeft>
        <FooterRight
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 1.1, duration: 0.8 }}
        >
          Hair & Make-up Artist · Muncih
        </FooterRight>
      </Footer>

      {/* ── Dev credit ── */}
      <DevStrip
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ delay: 1.3, duration: 0.9 }}
      >
        <DevLabel>Designed & developed by</DevLabel>
        <DevDivider />
        <DevLogoLink
          href="https://www.instagram.com/asitesagency/"
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.25 }}
        >
          <img src={devLogo} alt="Developer logo" />
        </DevLogoLink>
      </DevStrip>
      <Legal>
        <Link to={"/terms-and-conditions"} style={{ color: "white" }}>
          Terms and Conditions
        </Link>
      </Legal>
    </Section>
  );
}
